import type { SiteData } from '../../shared/types/site.js';
import { trackContactSubmission } from '../analytics.js';
import { t } from '../i18n.js';
import { renderPageHero } from './shared.js';
import { $, escapeHtml } from '../utils/index.js';

let stopContactPlaceholderAnimations: (() => void) | null = null;

function clearContactPlaceholderAnimations(): void {
  if (stopContactPlaceholderAnimations) {
    stopContactPlaceholderAnimations();
    stopContactPlaceholderAnimations = null;
  }
}

type PlaceholderTarget = {
  input: HTMLInputElement;
  placeholders: string[];
  phraseIndex: number;
};

function getUniquePlaceholders(placeholders: string[]): string[] {
  return Array.from(new Set(placeholders.filter((placeholder) => placeholder.trim().length > 0)));
}

function initSequencedPlaceholderAnimation(
  items: Array<{ input: HTMLInputElement | null; placeholders: string[] }>
): (() => void) | null {
  const targets: PlaceholderTarget[] = items
    .map(({ input, placeholders }) => {
      if (!input) return null;
      const uniquePlaceholders = getUniquePlaceholders(placeholders);
      if (uniquePlaceholders.length === 0) return null;
      return { input, placeholders: uniquePlaceholders, phraseIndex: 0 };
    })
    .filter((target): target is PlaceholderTarget => Boolean(target));

  if (targets.length === 0) return null;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    targets.forEach((target) => {
      target.input.placeholder = target.placeholders[0] ?? '';
    });
    return null;
  }

  const controller = new AbortController();
  const { signal } = controller;
  let timer: number | null = null;
  let targetIndex = 0;
  let charIndex = 0;

  const schedule = (delay: number) => {
    if (timer !== null) window.clearTimeout(timer);
    timer = window.setTimeout(tick, delay);
  };

  const advanceTarget = () => {
    targetIndex = (targetIndex + 1) % targets.length;
    charIndex = 0;
  };

  const isTargetAvailable = (target: PlaceholderTarget) =>
    document.activeElement !== target.input && target.input.value.length === 0;

  const tick = () => {
    if (document.hidden) {
      schedule(220);
      return;
    }

    const target = targets[targetIndex]!;
    if (!isTargetAvailable(target)) {
      advanceTarget();
      schedule(180);
      return;
    }

    const phrase = target.placeholders[target.phraseIndex] ?? '';

    charIndex = Math.min(charIndex + 1, phrase.length);
    target.input.placeholder = phrase.slice(0, charIndex);
    if (charIndex >= phrase.length) {
      target.phraseIndex = (target.phraseIndex + 1) % target.placeholders.length;
      advanceTarget();
      schedule(1450);
      return;
    }
    schedule(42);
  };

  targets.forEach((target) => {
    target.input.placeholder = target.placeholders[0] ?? '';
  });
  targets[targetIndex]!.input.placeholder = '';
  schedule(260);

  targets.forEach((target) => {
    target.input.addEventListener(
      'focus',
      () => {
        if (!target.input.value) target.input.placeholder = '';
      },
      { signal }
    );

    target.input.addEventListener(
      'blur',
      () => {
        if (!target.input.value) schedule(180);
      },
      { signal }
    );
  });

  return () => {
    controller.abort();
    if (timer !== null) {
      window.clearTimeout(timer);
      timer = null;
    }
  };
}

function initContactPlaceholderAnimations(root: HTMLElement): void {
  clearContactPlaceholderAnimations();

  const cleanup = initSequencedPlaceholderAnimation([
    {
      input: root.querySelector<HTMLInputElement>('#contact-email'),
      placeholders: [
        t('contact.form.email.placeholder'),
        'hello@company.com',
        'name@startup.com',
        'product@team.com',
        'founder@company.com',
      ],
    },
    {
      input: root.querySelector<HTMLInputElement>('#contact-phone'),
      placeholders: [
        t('contact.form.phone.placeholder'),
        '+355 69 123 4567',
        '+1 (212) 555-0147',
        '+49 1512 3456789',
        '+44 7700 900123',
      ],
    },
  ]);

  stopContactPlaceholderAnimations = cleanup;
}

export function renderContact(siteData: SiteData): void {
  const el = $('#page-contact-us');
  if (!el) return;

  const contact = siteData.contact ?? {};
  const industries = siteData.industries?.items ?? [];
  const serviceTopics = [
    'Web Development',
    'Mobile Apps',
    'Consulting',
    'Maintenance & Support',
  ];
  const serviceOptions = serviceTopics.map((topic) => `<option>${escapeHtml(topic)}</option>`).join('');
  const industryOptions = industries.map((item) => `<option>${escapeHtml(item.title)}</option>`).join('');
  const address = 'Str. Pajazit Ahmeti, Gjilan, 60000, Kosova';
  const mapsQuery = encodeURIComponent(address);
  const emailAddress = escapeHtml(contact.email ?? 'hello@arkron.al');

  el.innerHTML = `
    ${renderPageHero({
      label: t('contact.label'),
      title: t('contact.title'),
      intro: t('contact.intro'),
      kicker: 'Response style',
      stack: ['Clear next steps', 'Fast follow-up', 'Senior review'],
    })}
    <section class="section">
      <div class="container contact-layout">
        <div class="contact-form-wrap animate-on-scroll">
          <h2 class="section-title">${t('contact.form.title')}</h2>
          <form class="contact-form" id="contact-form">
            <div class="form-grid">
              <div class="form-group">
                <label for="contact-topic">${t('contact.form.topic')}</label>
                <select id="contact-topic">
                  <option>${t('contact.form.general')}</option>
                  <optgroup label="Services">
                    ${serviceOptions}
                  </optgroup>
                  <optgroup label="Industries">
                    ${industryOptions}
                  </optgroup>
                  <optgroup label="Other">
                    <option>${t('contact.form.job')}</option>
                    <option>${t('contact.form.other')}</option>
                  </optgroup>
                </select>
              </div>
              <div class="form-group">
                <label for="contact-email">${t('contact.form.email')}</label>
                <input id="contact-email" type="email" placeholder="${t('contact.form.email.placeholder')}" />
              </div>
              <div class="form-group">
                <label for="contact-phone">${t('contact.form.phone')}</label>
                <input id="contact-phone" type="tel" placeholder="${t('contact.form.phone.placeholder')}" />
              </div>
            </div>
            <div class="form-group">
              <label for="contact-message">${t('contact.form.message')}</label>
              <textarea id="contact-message" rows="6" placeholder="${t('contact.form.message.placeholder')}"></textarea>
            </div>
            <button type="submit" class="btn-primary btn-glass">${t('contact.form.send')}</button>
          </form>
        </div>
        <aside class="contact-side animate-on-scroll">
          <article class="contact-side-card">
            <p class="contact-side-kicker">${t('contact.email')}</p>
            <a href="mailto:${emailAddress}" class="contact-side-link">${emailAddress}</a>
            <p>Tell us the product stage, timeline, and what needs to change.</p>
          </article>
          <article class="contact-side-card">
            <p class="contact-side-kicker">${t('contact.address')}</p>
            <p>${escapeHtml(address)}</p>
            <a class="text-link" href="https://www.google.com/maps?q=${mapsQuery}" target="_blank" rel="noopener">${t('contact.viewmaps')}</a>
            <div class="contact-map">
              <iframe
                title="ARKRON location"
                loading="lazy"
                referrerpolicy="no-referrer-when-downgrade"
                src="https://www.google.com/maps?q=${mapsQuery}&z=15&output=embed">
              </iframe>
            </div>
          </article>
        </aside>
      </div>
    </section>
  `;

  const form = el.querySelector('#contact-form');
  if (form) {
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const topic = (el.querySelector('#contact-topic') as HTMLSelectElement | null)?.value?.trim() ?? '';
      const email = (el.querySelector('#contact-email') as HTMLInputElement | null)?.value?.trim() ?? '';
      const phone = (el.querySelector('#contact-phone') as HTMLInputElement | null)?.value?.trim() ?? '';
      const message = (el.querySelector('#contact-message') as HTMLTextAreaElement | null)?.value?.trim() ?? '';
      const to = contact.email ?? 'hello@arkron.al';
      const subject = `Contact form - ${topic || 'General'}`;
      const body = [
        `Topic: ${topic || 'General'}`,
        `Email: ${email || '-'}`,
        `Phone: ${phone || '-'}`,
        '',
        'Message:',
        message || '-',
      ].join('\n');
      trackContactSubmission(topic || 'General');
      window.location.href = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    });
  }

  initContactPlaceholderAnimations(el);
}
